import {
  BusinessNeedsCategory,
  CommercialRetailOfficeOption,
  ConsultationType,
  LargeCommercialIndustrialOption,
} from '../../../__generated__/pge-types';
import { convertValidationRules } from '../../../hooks/useFormState';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  validateEmail,
  validateNameField,
  validatePhone,
  validateRequiredAndValidCharacters,
  validateZipCode,
  validateAccountNumber,
  validateExistsAndAlphabetCharacters,
} from '../../../util/form-validation';
import { EnergyConsultationFormModel } from '../types';

export const createEnergyConsultationFormValidateFunction = () =>
  convertValidationRules<
    EnergyConsultationFormModel,
    { isPublicForm: boolean }
  >(context => {
    const { values, isPublicForm } = context;
    const { t } = useTranslation();

    const isOthersSelected =
      values?.commercialRetailOfficeOptions?.find(
        e => e === CommercialRetailOfficeOption.Other,
      ) ||
      values?.largeCommercialIndustrialOptions?.find(
        e => e === LargeCommercialIndustrialOption.Other,
      );

    return {
      serviceAddress: () =>
        !values.serviceAddress ? t('FIELD_IS_REQUIRED') : null,

      city: isPublicForm
        ? validateRequiredAndValidCharacters.bind(null, t('CITY').toLowerCase())
        : () => null,

      state: isPublicForm
        ? validateNameField.bind(null, t('STATE'))
        : () => null,

      postalCode: isPublicForm
        ? () => validateZipCode(values.postalCode!)
        : () => null,

      businessName: () =>
        !values.businessName ? `${t('ENTER_THE')} ${t('BUSINESS_NAME')}` : null,
      accountHolderName: () =>
        !values.accountHolderName
          ? `${t('ENTER_THE')} ${t('ACCOUNT_HOLDER_NAME')}.`
          : null,
      requestorFirstName: values.isRequestorNameFieldsVisible
        ? validateExistsAndAlphabetCharacters.bind(
            null,
            t('REQUESTOR_FIRSTNAME'),
          )
        : null,
      requestorLastName: values.isRequestorNameFieldsVisible
        ? validateExistsAndAlphabetCharacters.bind(
            null,
            t('REQUESTOR_LASTNAME'),
          )
        : null,
      jobTitle: () =>
        !values.jobTitle ? `${t('ENTER_THE')} ${t('JOB_TITLE')}.` : null,
      emailAddress: () => validateEmail(values.emailAddress),
      phoneNumber: () => validatePhone(values.phoneNumber),
      preferredLanguage: () =>
        !values.preferredLanguage
          ? `${t('PLEASE_SELECT')} ${t('PREFERRED_LANGUAGE')}`
          : null,
      consultationType: () =>
        !values.consultationType
          ? `${t('PLEASE_SELECT')} ${t('CONSULTATION_TYPE')}`
          : null,
      scheduleDate: () =>
        values.consultationType === ConsultationType.SiteVisit &&
        !values.scheduleDate
          ? `${t('PLEASE_SELECT')} ${t('DATE')}`
          : null,
      scheduleTime: () =>
        values.consultationType === ConsultationType.SiteVisit &&
        !values.scheduleTime
          ? `${t('PLEASE_SELECT')} ${t('TIME')}`
          : null,
      businessNeedsCategory: () =>
        !values.businessNeedsCategory
          ? `${t('PLEASE_SELECT_AN_OPTION')}`
          : null,
      commercialRetailOfficeOptions: () =>
        values.businessNeedsCategory ===
          BusinessNeedsCategory.CommercialRetailOffice &&
        values.commercialRetailOfficeOptions.length === 0
          ? `${t('PLEASE_SELECT_YOUR_NEEDS')}`
          : null,
      largeCommercialIndustrialOptions: () =>
        values.businessNeedsCategory ===
          BusinessNeedsCategory.LargeCommercialIndustrial &&
        values.largeCommercialIndustrialOptions.length === 0
          ? `${t('PLEASE_SELECT_YOUR_NEEDS')}`
          : null,

      otherBusinessNeed: () =>
        isOthersSelected && !values.otherBusinessNeed
          ? t('FIELD_IS_REQUIRED')
          : null,

      accountNumber: () =>
        isPublicForm
          ? validateAccountNumber(values.accountNumber)
          : !values.accountNumber
          ? t('SELECT_AN_ACCOUNT')
          : null,
    };
  });
